// ============================================================================
// PROJECT OUTPUTS HOOKS
// React hooks for project outputs management
// ============================================================================

import { useQuery, useMutation, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { api } from '../services/api';

// ============================================================================
// TYPES
// ============================================================================

export interface ProjectOutput {
  outputId: string;
  projectId: string;
  sessionId?: string;
  name: string;
  type: 'avatar' | 'workflow' | 'requirement' | 'user_story' | 'transcript';
  avatarRole?: string;
  format: 'json' | 'markdown' | 'pdf' | 'csv';
  s3Key: string;
  fileSizeBytes: number;
  createdAt: string;
  createdBy: string;
}

export interface OutputFilters {
  type?: string;
  sessionId?: string;
  avatarRole?: string;
}

export interface OutputContent {
  content: any;
  format: string;
}

export interface DownloadUrlResponse {
  downloadUrl: string;
  expiresAt: string;
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

const outputsApi = {
  // List outputs
  listOutputs: async (
    projectId: string,
    filters: OutputFilters | undefined
  ): Promise<ProjectOutput[]> => {
    const params = new URLSearchParams();
    if (filters?.type) params.append('type', filters.type);
    if (filters?.sessionId) params.append('sessionId', filters.sessionId);
    if (filters?.avatarRole) params.append('avatarRole', filters.avatarRole);

    const response = await api.get(
      `/api/projects/${projectId}/outputs?${params.toString()}`
    );
    return response.data.outputs;
  },

  // Get output details
  getOutput: async (
    projectId: string,
    outputId: string
  ): Promise<ProjectOutput> => {
    const response = await api.get(
      `/api/projects/${projectId}/outputs/${outputId}`
    );
    return response.data.output;
  },

  // Get output content
  getOutputContent: async (
    projectId: string,
    outputId: string
  ): Promise<OutputContent> => {
    const response = await api.get(
      `/api/projects/${projectId}/outputs/${outputId}/content`
    );
    return { content: response.data.content, format: response.data.format };
  },

  // Get download URL
  getDownloadUrl: async (
    projectId: string,
    outputId: string
  ): Promise<DownloadUrlResponse> => {
    const response = await api.get(
      `/api/projects/${projectId}/outputs/${outputId}/download`
    );
    return response.data;
  },

  // Delete output
  deleteOutput: async (
    projectId: string,
    outputId: string
  ): Promise<void> => {
    await api.delete(
      `/api/projects/${projectId}/outputs/${outputId}`
    );
  }
};

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Hook to list all outputs for a project
 */
export const useProjectOutputs = (
  projectId: string,
  token: string,
  filters?: OutputFilters
): UseQueryResult<ProjectOutput[], Error> => {
  return useQuery({
    queryKey: ['project-outputs', projectId, filters],
    queryFn: () => outputsApi.listOutputs(projectId, filters),
    enabled: !!projectId && !!token
  });
};

/**
 * Hook to get a specific output
 */
export const useProjectOutput = (
  projectId: string,
  outputId: string,
  token: string
): UseQueryResult<ProjectOutput, Error> => {
  return useQuery({
    queryKey: ['project-output', projectId, outputId],
    queryFn: () => outputsApi.getOutput(projectId, outputId),
    enabled: !!projectId && !!outputId && !!token
  });
};

/**
 * Hook to get output content (for viewer)
 */
export const useOutputContent = (
  projectId: string,
  outputId: string,
  token: string,
  enabled: boolean = true
): UseQueryResult<OutputContent, Error> => {
  return useQuery({
    queryKey: ['output-content', projectId, outputId],
    queryFn: () => outputsApi.getOutputContent(projectId, outputId),
    enabled: enabled && !!projectId && !!outputId && !!token
  });
};

/**
 * Hook to download an output
 */
export const useDownloadOutput = (projectId: string, token: string) => {
  return useMutation({
    mutationFn: async (outputId: string) => {
      const { downloadUrl } = await outputsApi.getDownloadUrl(projectId, outputId);
      // Trigger browser download
      window.open(downloadUrl, '_blank');
    }
  });
};

/**
 * Hook to delete an output
 */
export const useDeleteOutput = (projectId: string, token: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (outputId: string) =>
      outputsApi.deleteOutput(projectId, outputId),
    onSuccess: () => {
      // Invalidate outputs list to trigger refetch
      queryClient.invalidateQueries({ queryKey: ['project-outputs', projectId] });
    }
  });
};

/**
 * Utility hook to format file sizes
 */
export const useFormatFileSize = () => {
  return (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };
};

/**
 * Utility hook to format output type labels
 */
export const useFormatOutputType = () => {
  const typeLabels: Record<string, string> = {
    avatar: 'Avatar Output',
    workflow: 'Workflow Result',
    requirement: 'Requirements',
    user_story: 'User Stories',
    transcript: 'Transcript'
  };

  return (type: string): string => {
    return typeLabels[type] || type;
  };
};

